import { getPortalEmoji } from './portalEmojis';
import { CRM_OWNER_EXECUTIVE_ROLES } from '../services/accessControlService';

/**
 * CRM ▸ Reports catalog: the hub tiles, the route guards (ReportAccessGate) and the
 * access editor all read this.
 *
 * Who may open a report beyond Owners/Executives (who always can) lives in
 * reportAccess/{reportId}: { everyone, userIds, updatedAt, updatedBy }. With no doc yet,
 * defaultAccess applies: 'all' for reports that were open to every staff member before
 * access became editable, 'admin' for the ones that were Owner/Executive only.
 */
export const REPORT_GROUPS = [
  {
    id: 'growth',
    emoji: '📈',
    title: 'Growth',
    reports: [
      { id: 'marketing', title: 'Marketing', emoji: getPortalEmoji('marketing'), path: '/crm/marketing-reports', description: 'Lead performance, expenditure, and ROI', defaultAccess: 'admin' },
      { id: 'sales', title: 'Sales', emoji: getPortalEmoji('sales'), path: '/crm/sales-reports', description: 'Sales metrics, pipeline and conversion data', defaultAccess: 'admin' },
      { id: 'lost-leads', title: 'Leads', emoji: '🌱', path: '/crm/lost-leads-reports', description: 'All leads, won and lost, with trends and insights', defaultAccess: 'admin' },
      { id: 'lead-portal', title: 'Lead Portal', emoji: '🌐', path: '/crm/lead-portal-reports', description: 'Start portal traffic, engagement events, and per-lead activity', defaultAccess: 'admin' },
    ],
  },
  {
    id: 'clients',
    emoji: '🤝',
    title: 'Clients',
    reports: [
      { id: 'lost-clients', title: 'Clients', emoji: '🤝', path: '/crm/lost-clients-reports', description: 'All clients, active and lost, with retention insights', defaultAccess: 'admin' },
      { id: 'client-satisfaction', title: 'Client Check Ins', emoji: '⭐', path: '/crm/client-satisfaction', description: 'Client check-in ratings, feedback, and response tracking', defaultAccess: 'all' },
      { id: 'client-purchases', title: 'Client Purchases', emoji: '🛒', path: '/crm/client-purchases', description: 'Client purchase history and transaction analytics', defaultAccess: 'all' },
      { id: 'breakthroughs', title: 'Breakthroughs', emoji: '⚡', path: '/crm/breakthroughs-reports', description: 'Key achievements, milestones, and breakthrough moments', defaultAccess: 'admin' },
    ],
  },
  {
    id: 'team',
    emoji: '🧭',
    title: 'Team & Delivery',
    reports: [
      { id: 'meetings', title: 'Meetings', emoji: '🎥', path: '/crm/meetings-reports', description: 'Saved Meeting Mode reports with the Fathom recording and action items assigned to the team', defaultAccess: 'admin' },
      { id: 'call-reports', title: 'Call Reports', emoji: '📞', path: '/crm/call-reports', description: 'Implementation Hours calls synced from Calendly + Google Calendar: by advisor, by client, and hours-log reconciliation', defaultAccess: 'admin' },
      { id: 'goals', title: 'Goals', emoji: getPortalEmoji('goals'), path: '/crm/company-goals', description: 'Track progress on team and company goals', defaultAccess: 'all' },
      { id: 'technology', title: 'Technology', emoji: getPortalEmoji('technology'), path: '/crm/technology-reports', description: 'Systems, infrastructure, and tooling analytics', defaultAccess: 'admin' },
      { id: 'staff-activity', title: 'Staff Activity', emoji: '📋', path: '/crm/staff-activity', description: 'CRM logins, account views, and changes by internal team members', defaultAccess: 'admin' },
    ],
  },
  {
    id: 'business',
    emoji: '🏛️',
    title: 'Business & Events',
    reports: [
      { id: 'finance', title: 'Finance', emoji: getPortalEmoji('finance'), path: '/crm/finance-reports', description: 'Revenue, profit margins and expense analysis', defaultAccess: 'admin' },
      { id: 'summit', title: 'Summit', emoji: '🏆', path: '/crm/summit-overview', description: 'BBA Annual Summit registration and analytics', defaultAccess: 'all' },
      { id: 'conferences', title: 'Conferences', emoji: '🎤', path: '/crm/conferences-reports', description: 'Conference attendance, presentations, and networking analytics', defaultAccess: 'admin' },
    ],
  },
];

export const REPORTS_BY_ID = Object.fromEntries(
  REPORT_GROUPS.flatMap((group) => group.reports.map((report) => [report.id, { ...report, groupId: group.id }]))
);

export const isReportAdminRole = (role) => CRM_OWNER_EXECUTIVE_ROLES.includes(role);

// The saved access for a report, or its default when nobody has edited it yet.
export const effectiveReportAccess = (report, accessDoc) => ({
  everyone: accessDoc ? accessDoc.everyone === true : report?.defaultAccess === 'all',
  userIds: Array.isArray(accessDoc?.userIds) ? accessDoc.userIds : [],
});

export const canOpenReport = (report, accessDoc, role, uid) => {
  if (isReportAdminRole(role)) return true;
  if (!report) return false;
  const access = effectiveReportAccess(report, accessDoc);
  return access.everyone || (!!uid && access.userIds.includes(uid));
};
